// Workspace settings (spec §16.3), for owners and admins: rename, leave, and
// (owners) delete the workspace.
import type { MembersResponse, WorkspaceInfo } from '../../shared/api.ts';
import { ApiError, del, get, patch } from '../api.ts';
import { h } from '../util/dom.ts';
import { typedConfirm } from './confirm-dialog.ts';
import { busy, field, formError, getMe, header, mount, notice, rememberWorkspace, setTitle } from './page.ts';

export async function settingsPage(slug: string): Promise<void> {
  const me = await getMe();
  const ws = me.workspaces.find((w) => w.slug === slug);
  if (!ws || (ws.role !== 'owner' && ws.role !== 'admin')) throw new ApiError(404, 'Workspace not found.');
  rememberWorkspace(ws.slug);
  setTitle(`Settings · ${ws.name}`);
  const members = await get<MembersResponse>(`/api/workspaces/${ws.id}/members`);
  const owners = members.members.filter((m) => m.role === 'owner').length;

  const flash = h('div');
  const section = (title: string, ...children: Node[]) => h('section', { class: 'account-section' }, [h('h2', {}, [title]), ...children]);
  const canLeave = ws.role !== 'owner' || owners > 1;
  mount(header(me, ws, 'settings'), h('main', { class: 'page narrow' }, [
    h('div', { class: 'page-head' }, [h('h1', {}, ['Settings'])]),
    flash,
    h('div', { class: 'stack-lg' }, [
      section('Name', renameForm(), h('p', { class: 'muted' }, [
        `Its address stays ${location.origin}/w/${ws.slug} when you rename it, so links keep working.`,
      ])),
      ...(canLeave ? [section('Leave workspace', ...leave())] : []),
      ...(ws.role === 'owner' ? [section('Delete workspace', ...remove(ws))] : []),
    ]),
  ]));

  function renameForm(): HTMLElement {
    const name = h('input', { type: 'text', required: '', maxlength: '80', value: ws!.name });
    const save = h('button', { type: 'submit', class: 'btn' }, ['Save']);
    const error = formError();
    const form = h('form', { class: 'inline-form' }, [field('Workspace name', name), save, error]);
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      error.clear();
      void busy(save, async () => {
        try {
          const res = await patch<WorkspaceInfo>(`/api/workspaces/${ws!.id}`, { name: name.value });
          const option = document.querySelector<HTMLOptionElement>(`.ws-switcher option[value="${CSS.escape(res.slug)}"]`);
          if (option) option.textContent = res.name;
          setTitle(`Settings · ${res.name}`);
          flash.replaceChildren(notice([`Renamed to ${res.name}.`]));
        } catch (err) {
          error.show(err);
        }
      });
    });
    return form;
  }

  function leave(): Node[] {
    const button = h('button', { type: 'button', class: 'btn' }, ['Leave workspace']);
    const error = formError();
    button.addEventListener('click', () => {
      if (!confirm(`Leave ${ws!.name}? You'll lose access to its boards.`)) return;
      void busy(button, async () => {
        try {
          await del(`/api/workspaces/${ws!.id}/members/${me.user.id}`);
          location.assign('/');
        } catch (err) {
          error.show(err);
        }
      });
    });
    return [h('p', { class: 'muted' }, ["You'll lose access to its boards. Someone will need to invite you to come back."]), button, error];
  }

  function remove(w: WorkspaceInfo): Node[] {
    if (w.billingStatus === 'active' || w.billingStatus === 'past_due') {
      return [h('p', { class: 'muted' }, [
        'It has an active subscription, so it can’t be deleted yet. Cancel the subscription from the ',
        h('a', { href: `/w/${w.slug}/plan` }, ['Plan page']), ' first; once it has ended, the workspace can be deleted.',
      ])];
    }
    const button = h('button', { type: 'button', class: 'btn danger-outline' }, ['Delete workspace…']);
    button.addEventListener('click', () => typedConfirm({
      title: `Delete ${w.name}?`, confirmText: w.name, action: 'Delete workspace',
      explain: [
        ["All its boards are deleted, with their history and anything in its trash. Everyone in it loses access, and its admins and members are told by email."],
        ["This can't be undone."],
      ],
      run: async () => {
        await del(`/api/workspaces/${w.id}`, { redirectOn401: false });
        location.assign('/');
      },
    }));
    return [h('p', { class: 'muted' }, ['Deletes the workspace and everything in it, for everyone.']), button];
  }
}
